import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Form, Button, Spinner, Alert, Badge } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageLayout from "../../layouts/PageLayout";
import GalleryUpload from "./UploadImages";

export default function ListerEditProductPage() {

    const navigate = useNavigate();
    const location = useLocation();

    // Extract location state item if passed via navigation
    const passedItem = location.state?.item;

    // =========================================================
    // STATES
    // =========================================================

    const [loading, setLoading] = useState(!passedItem);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Single Form Data state initialized from location state (if exists)
    const [formData, setFormData] = useState({
        id: passedItem?.id || "",
        product_code: passedItem?.product_code || "",
        title: passedItem?.title || "",
        // category_id: passedItem?.category?.id || "",
        // category_name: passedItem?.category?.name || "",
        // brand_id: passedItem?.brand?.id || "",
        // brand_name: passedItem?.brand?.name || "",
        product_qty: passedItem?.product_qty ?? 1,
        product_condition: passedItem?.product_condition || "new",
        selling_style: passedItem?.selling_style || "Fixed",
        buying_price: passedItem?.buying_price ?? "",
        sell_price_dollar: passedItem?.sell_price_dollar ?? "",
        short_description: passedItem?.short_description || "",
        description: passedItem?.description || "",

    });

    // Main image states
    const [mainImage, setMainImage] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState(process.env.REACT_APP_IMG_URL + passedItem.card_img);
    const [firstImagePreview, setFirstImagePreview] = useState(passedItem.first_img);

    // Gallery image states
    const [existingImages, setExistingImages] = useState(passedItem?.images || []);
    const [deletedImages, setDeletedImages] = useState([]);
    const [galleryImages, setGalleryImages] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);

    // =========================================================
    // AUTH HEADERS
    // =========================================================

    const authHeaders = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    };

    // =========================================================
    // INPUT CHANGE HANDLER
    // =========================================================

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Explicit handler for ReactQuill rich text editor
    const handleQuillChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            description: value,
        }));
    };


    // =========================================================
    // IMAGE HANDLERS
    // =========================================================

    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
    };

    const handleGalleryImages = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setGalleryImages((prev) => [...prev, ...files]);
        const previews = files.map((file) => ({
            file,
            url: URL.createObjectURL(file),
        }));
        setGalleryPreviews((prev) => [...prev, ...previews]);
        e.target.value = "";
    };

    const removeGalleryImage = (index) => {
        setGalleryImages((prev) => prev.filter((_, i) => i !== index));
        setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
    };

    const removeExistingImage = (image) => {
        if (image?.id) {
            setDeletedImages((prev) => [...prev, image.id]);
        }
        setExistingImages((prev) => prev.filter((item) => item.id !== image.id));
    };

    // =========================================================
    // VALIDATION
    // =========================================================

    const validateForm = () => {
        if (!formData.title.trim()) {
            setError("Product title is required.");
            return false;
        }

        if (formData.buying_price === "" || Number(formData.buying_price) < 0) {
            setError("Please enter a valid Buying Price.");
            return false;
        }

        return true;
    };

    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;

        setSaving(true);

        try {
            const submitData = new FormData();

            // Append all keys from formData state
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    submitData.append(key, formData[key]);
                }
            });

            // Images
            if (mainImage) {
                submitData.append("card_img", mainImage);
            }



            // Laravel method spoofing
            submitData.append("_method", "PUT");

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/lister/products/${formData.id}`,
                submitData,
                {
                    headers: {
                        ...authHeaders,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const resData = response.data;

            if (!resData.success) {
                setError(resData.message || "Failed to update product.");
                return;
            }

            setSuccess("Product updated successfully.");
            setMainImage(null);
            setGalleryImages([]);
            setGalleryPreviews([]);
            setDeletedImages([]);

            navigate("/lister-products");

        } catch (err) {
            console.error("Update product error:", err);
            if (err.response?.data?.errors) {
                const messages = Object.values(err.response.data.errors).flat().join(" ");
                setError(messages);
            } else {
                setError(err.response?.data?.message || "Failed to update product.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Quill config
    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["blockquote", "code-block"],
            ["link", "image"],
            ["clean"],
        ],
    };

    const quillFormats = [
        "header", "bold", "italic", "underline", "strike",
        "color", "background", "list", "bullet", "align",
        "blockquote", "code-block", "link", "image",
    ];

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-3 text-muted">Loading product...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                {/* Breadcrumb */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Edit Product</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Home</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Products</li>
                                <li className="mc-breadcrumb-item">Edit Product</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Alerts */}
                <Col xl={12}>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}
                </Col>

                {/* Header Info */}
                <Col xl={12}>
                    <Card className="mb-4 shadow-sm">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={2} className="mb-3 mb-md-0">
                                    <div style={{ width: "100%", height: "120px", borderRadius: "10px", overflow: "hidden", background: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {firstImagePreview ? (
                                            <img src={process.env.REACT_APP_IMG_URL + firstImagePreview} alt={formData.title} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                        ) : (
                                            <span className="text-muted">No Image</span>
                                        )}
                                    </div>
                                </Col>
                                <Col md={7}>
                                    <small className="text-muted">Product Code</small>
                                    <h5 className="mb-2">{formData.product_code || "N/A"}</h5>
                                    {formData.category_name && <Badge bg="secondary" className="me-2">{formData.category_name}</Badge>}
                                    {formData.brand_name && <Badge bg="info">{formData.brand_name}</Badge>}
                                </Col>
                                <Col md={3} className="text-md-end mt-3 mt-md-0">
                                    <small className="text-muted d-block">Selling Style</small>
                                    <strong>{formData.selling_style}</strong>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Main Form */}
                <Col xl={12}>
                    <Form onSubmit={handleSubmit}>
                        <Card className="mb-4 shadow-sm">
                            <Card.Header>
                                <h5 className="mb-0">Product Information</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {/* TITLE */}
                                    <Col md={8}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                placeholder="Enter product title"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Buying Price */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="buying_price"
                                                value={formData.buying_price}
                                                onChange={handleChange}
                                                placeholder="0.00"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* DOLLAR PRICE */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sell Price ($)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="sell_price_dollar"
                                                value={formData.sell_price_dollar}
                                                onChange={handleChange}
                                                placeholder="0.00"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* QUANTITY */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="product_qty"
                                                value={formData.product_qty}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* CONDITION */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Condition</Form.Label>
                                            <Form.Select name="product_condition" value={formData.product_condition} onChange={handleChange}>
                                                <option value="new">New</option>
                                                <option value="used">Used</option>
                                                <option value="refurbished">Refurbished</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* SHORT DETAILS */}
                                    <Col xs={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Short Details</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="short_description"
                                                value={formData.short_description}
                                                onChange={handleChange}
                                                placeholder="Enter short details..."
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* MAIN IMAGE */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Main Product Image</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4} lg={3}>
                                        <div style={{ width: "100%", height: "220px", border: "1px dashed #ced4da", borderRadius: "10px", overflow: "hidden", background: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            {mainImagePreview ? (
                                                <img src={mainImagePreview} alt="Main" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                            ) : (
                                                <span className="text-muted">No Image</span>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={8}>
                                        <Form.Group>
                                            <Form.Label>Replace Main Image</Form.Label>
                                            <Form.Control type="file" accept="image/*" onChange={handleMainImageChange} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* GALLERY */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product Gallery</h5></Card.Header>
                            <Card.Body>
                                <GalleryUpload pId={formData.id} images={existingImages} />
                            </Card.Body>
                        </Card>

                        {/* LONG DESCRIPTION */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Long Product Details</h5></Card.Header>
                            <Card.Body>
                                <Form.Group>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.description}
                                        onChange={handleQuillChange}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Write complete product description..."
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        {/* SUBMIT BUTTONS */}
                        <Card className="mb-5 shadow-sm">
                            <Card.Body>
                                <div className="d-flex justify-content-end gap-2">
                                    <Button type="button" variant="secondary" onClick={() => navigate(-1)} disabled={saving}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" variant="primary" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-2" />
                                                Updating...
                                            </>
                                        ) : (
                                            "Update Product"
                                        )}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Form>
                </Col>
            </Row>
        </PageLayout>
    );
}